import { useCallback, useMemo } from 'react';
import {
  useHarEntryDisplayWithPreview,
  type HarPreviewDecodeFn,
} from '@terra/ui-design-system';
import type { StoredHarDisplayEntry } from '@terra/shared-utils';
import { GRPC_WEB_SUPPORT_ENABLED_FLAG } from '@terra/shared-contracts-feature-flags';
import { useFeatureFlags } from './useFeatureFlags';
import {
  buildStoredHarDisplayEntry,
  fetchGrpcHarPreviewDecode,
  isGrpcHarEntry,
} from '../utils/harGrpcDisplay.utils';

export function useHarEntryBodyView({
  applicationId,
  harEntry: sourceHarEntry,
  viewResetKey,
}: {
  applicationId?: string;
  harEntry?: StoredHarDisplayEntry | null;
  viewResetKey?: string;
}) {
  const { isFeatureFlagEnabled: protocolDecodeEnabled } = useFeatureFlags(
    GRPC_WEB_SUPPORT_ENABLED_FLAG,
  );

  const baseHarEntry = useMemo((): StoredHarDisplayEntry | null => {
    if (!sourceHarEntry || !isGrpcHarEntry(sourceHarEntry)) {
      return null;
    }

    return sourceHarEntry;
  }, [sourceHarEntry]);

  const decodePreview = useCallback<HarPreviewDecodeFn>(
    ({ applicationId: harApplicationId, harEntry }) =>
      fetchGrpcHarPreviewDecode({
        applicationId: harApplicationId,
        harEntry,
        enabled: protocolDecodeEnabled,
      }),
    [protocolDecodeEnabled],
  );

  const display = useHarEntryDisplayWithPreview({
    harEntry: baseHarEntry,
    applicationId,
    viewResetKey,
    decodePreview,
    protocolDecodeEnabled,
  });

  return {
    bodyView: display.bodyView,
    setBodyView: display.setBodyView,
    harEntry: display.harEntry,
    hasHarEntry: Boolean(baseHarEntry),
    hasDecodedBodyView: display.hasDecodedBodyView,
    showProtocolBodyViewToggle: display.showProtocolBodyViewToggle,
    editorProps: display.editorProps,
    requestContent: display.requestContent,
    responseContent: display.responseContent,
  };
}

export function useHarEntryBodyViewFromShape({
  applicationId,
  harEntryShape,
  viewResetKey,
}: {
  applicationId?: string;
  harEntryShape?: {
    request: StoredHarDisplayEntry['request'];
    response?: StoredHarDisplayEntry['response'];
    webGrpcDecodedData?: StoredHarDisplayEntry['webGrpcDecodedData'];
  } | null;
  viewResetKey?: string;
}) {
  const sourceHarEntry = useMemo(
    () =>
      harEntryShape
        ? (buildStoredHarDisplayEntry(harEntryShape) ?? null)
        : null,
    [harEntryShape],
  );

  return useHarEntryBodyView({
    applicationId,
    harEntry: sourceHarEntry,
    viewResetKey,
  });
}
